import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import ChatService from '../services/ChatService';
import ChatMessageBubble from '../components/ChatMessageBubble';
import ChatInputField from '../components/ChatInputField';
import TypingIndicator from '../components/TypingIndicator';
import ImagePreviewModal from '../components/ImagePreviewModal';
import MediaGalleryModal from '../components/MediaGalleryModal';
import soundNotification from '../utils/soundNotification';
import browserNotifications from '../utils/browserNotifications';
import ImageCompression from '../utils/imageCompression';
import { theme } from '../styles/theme';
import './ChatRoomScreen.css';

/**
 * ChatRoomScreen - Main chat room component
 * Matches Flutter ChatRoomScreen design
 */
const ChatRoomScreen = ({ room, currentUserId, currentUserName, currentUserRole, onBack, showBackButton = true, onlineUsers: sharedOnlineUsers, setOnlineUsers: setSharedOnlineUsers }) => {
  const [messages, setMessages] = useState([]);
  const [forceUpdate, setForceUpdate] = useState(0); // Force re-render trigger
  const [isLoading, setIsLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUserName, setTypingUserName] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isOtherUserOnline, setIsOtherUserOnline] = useState(false);
  // Use shared onlineUsers if provided, otherwise fallback to local state
  const [localOnlineUsers, setLocalOnlineUsers] = useState(new Set());
  const onlineUsers = sharedOnlineUsers || localOnlineUsers;
  const setOnlineUsers = setSharedOnlineUsers || setLocalOnlineUsers;
  const [onlineUsersKey, setOnlineUsersKey] = useState(0); // Key to trigger re-renders when Set changes
  const onlineUsersRef = useRef(new Set()); // Ref to access latest onlineUsers in callbacks
  const otherParticipantRef = useRef(null); // Use ref to always have latest otherParticipant
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isUserScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const pendingOptimisticMessagesRef = useRef(new Map()); // Track optimistic messages by content
  const isInitialLoadRef = useRef(true); // Track if this is the initial load
  const scrollAnimationFrameRef = useRef(null); // For smooth scrolling
  const pendingScrollTimeoutsRef = useRef([]); // Track all pending scroll timeouts to cancel them
  const isScrollingProgrammaticallyRef = useRef(false); // Track if we're programmatically scrolling
  const messageListenerRef = useRef(null); // Store the listener callback to prevent recreation

  // Find other participant with proper handling
  const findOtherParticipant = () => {
    // Try using helper method first
    if (room.getOtherParticipant && typeof room.getOtherParticipant === 'function') {
      const found = room.getOtherParticipant(currentUserId);
      if (found && found.userName) return found;
    }

    // Fallback: find participant that's not current user
    if (room.participants && Array.isArray(room.participants)) {
      const searchUserId = String(currentUserId || '');
      const found = room.participants.find(p => {
        const pUserId = String(p.userId || p.user_id || '');
        return pUserId !== searchUserId;
      });
      if (found && found.userName) return found;

      // If still not found and user is admin, find driver or non-admin participant
      if (found && !found.userName) {
        const driverOrOther = room.participants.find(p => {
          const pUserId = String(p.userId || p.user_id || '');
          return (p.userRole === 'driver' || p.userRole !== 'admin') &&
            pUserId !== searchUserId &&
            p.userName;
        });
        if (driverOrOther) return driverOrOther;
      }
    }

    return null;
  };

  const otherParticipant = findOtherParticipant();
  
  // Update refs whenever they change
  useEffect(() => {
    otherParticipantRef.current = otherParticipant;
  }, [otherParticipant]);
  
  // Update onlineUsers ref and trigger key update when Set size changes
  useEffect(() => {
    const currentOnlineUsers = sharedOnlineUsers || onlineUsers;
    onlineUsersRef.current = currentOnlineUsers;
    // Use Set size as a way to detect changes (since Set objects can't be compared)
    setOnlineUsersKey(currentOnlineUsers.size);
  }, [onlineUsers.size, sharedOnlineUsers?.size]);
  
  // Check online status when onlineUsers Set or otherParticipant changes
  useEffect(() => {
    const currentOtherParticipant = otherParticipantRef.current || otherParticipant;
    const currentOnlineUsers = sharedOnlineUsers || onlineUsersRef.current || onlineUsers;
    
    if (currentOtherParticipant) {
      const otherUserId = currentOtherParticipant.userId || currentOtherParticipant.user_id;
      if (otherUserId) {
        const userIdStr = String(otherUserId);
        let isOnline = false;
        
        // Check if userId exists in Set (handles type mismatches)
        // Also check for admin format (admin_agencyId) and numeric formats
        if (currentOnlineUsers && currentOnlineUsers.size > 0) {
          currentOnlineUsers.forEach(id => {
            const idStr = String(id);
            // Direct match
            if (idStr === userIdStr) {
              isOnline = true;
            }
            // Also check if one is numeric and other is string of same number
            else if (Number(idStr) === Number(userIdStr) && !isNaN(Number(idStr)) && !isNaN(Number(userIdStr))) {
              isOnline = true;
            }
          });
        }
        
        setIsOtherUserOnline(isOnline);
      } else {
        setIsOtherUserOnline(false);
      }
    } else {
      setIsOtherUserOnline(false);
    }
  }, [onlineUsersKey, otherParticipant, sharedOnlineUsers?.size]);

  useEffect(() => {
    // CRITICAL: Reset scroll state when room changes - ensure we show latest messages
    isInitialLoadRef.current = true;
    isUserScrollingRef.current = false;
    isScrollingProgrammaticallyRef.current = false; // Reset programmatic scroll flag
    lastScrollTopRef.current = 0;

    // Ensure socket is connected before setting up listeners
    const ensureSocketConnected = () => {
      if (!ChatService.socket || !ChatService.socket.connected) {
        // Wait a bit and check again
        setTimeout(() => {
          if (ChatService.socket && ChatService.socket.connected) {
            joinRoom();
            setupListeners();
          } else {
            // Set up listeners anyway - they'll work when socket connects
            joinRoom();
            setupListeners();
          }
        }, 1000);
      } else {
        joinRoom();
        setupListeners();
      }
    };

    loadMessages();
    ensureSocketConnected();
    
    // Set up a gentle polling mechanism as fallback (only when socket disconnected)
    // Use a longer interval to prevent screen blinking
    let pollInterval = null;
    const startPolling = () => {
      if (pollInterval) return; // Already polling
      
      pollInterval = setInterval(() => {
        // Only poll if socket is not connected AND page is visible
        if ((!ChatService.socket || !ChatService.socket.connected) && !document.hidden) {
          // Skip loading state to prevent screen blinking
          loadMessages(true);
        } else if (ChatService.socket && ChatService.socket.connected) {
          // Socket reconnected, stop polling
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }
      }, 20000); // Poll every 20 seconds (much less aggressive, prevents blinking)
    };
    
    // Only start polling if socket is not connected
    if (!ChatService.socket || !ChatService.socket.connected) {
      startPolling();
    }

    // Initialize audio on first user interaction (required by browsers)
    const initializeAudio = () => {
      soundNotification.initializeOnUserInteraction();
      // Remove listener after first interaction
      document.removeEventListener('click', initializeAudio);
      document.removeEventListener('touchstart', initializeAudio);
      document.removeEventListener('keydown', initializeAudio);
    };

    // Listen for first user interaction to enable audio
    document.addEventListener('click', initializeAudio, { once: true });
    document.addEventListener('touchstart', initializeAudio, { once: true });
    document.addEventListener('keydown', initializeAudio, { once: true });

    // Setup scroll detection to prevent auto-scroll during manual scrolling
    const container = scrollContainerRef.current;
    if (container) {
      const handleScroll = () => {
        if (!container) return;

        const currentScrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        const scrollDelta = Math.abs(currentScrollTop - lastScrollTopRef.current);

        // If programmatic scroll flag is set, reset it after a short delay
        // This allows the scroll event to complete before we detect user scrolling
        if (isScrollingProgrammaticallyRef.current) {
          // Reset flag after scroll completes
          setTimeout(() => {
            isScrollingProgrammaticallyRef.current = false;
          }, 50);
          // Update last scroll position but don't treat as user scroll
          lastScrollTopRef.current = currentScrollTop;
          return; // Exit early - don't process as user scroll
        }

        // Only detect user scrolling if there's a significant scroll change (more than 3px)
        // This prevents detecting tiny scroll adjustments as user scrolling
        if (scrollDelta > 3) {
          // Detect if user is scrolling (but not during initial load)
          if (!isInitialLoadRef.current) {
            isUserScrollingRef.current = true;

            // Cancel all pending auto-scroll attempts when user starts scrolling
            pendingScrollTimeoutsRef.current.forEach(timeoutId => {
              clearTimeout(timeoutId);
            });
            pendingScrollTimeoutsRef.current = [];

            // Cancel any pending scroll animation frames
            if (scrollAnimationFrameRef.current) {
              cancelAnimationFrame(scrollAnimationFrameRef.current);
              scrollAnimationFrameRef.current = null;
            }
          }
        }

        // Detect if user scrolled to bottom (within 20px)
        const isAtBottom = scrollHeight - currentScrollTop - clientHeight < 20;
        if (isAtBottom) {
          // User scrolled to bottom, allow auto-scroll again
          isUserScrollingRef.current = false;
        }

        lastScrollTopRef.current = currentScrollTop;

        // Reset user scrolling flag after 1 second of no scrolling
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
          // Only reset if we are at the bottom
          if (container && container.scrollHeight - container.scrollTop - container.clientHeight < 20) {
            isUserScrollingRef.current = false;
          }
        }, 1000);
      };

      container.addEventListener('scroll', handleScroll, { passive: true });

      const handleWheel = () => {
        // Don't set user scrolling during initial load - we want to scroll to bottom
        if (!isInitialLoadRef.current) {
          isUserScrollingRef.current = true;

          // Cancel all pending auto-scroll attempts when user uses wheel
          pendingScrollTimeoutsRef.current.forEach(timeoutId => {
            clearTimeout(timeoutId);
          });
          pendingScrollTimeoutsRef.current = [];

          // Cancel any pending scroll animation frames
          if (scrollAnimationFrameRef.current) {
            cancelAnimationFrame(scrollAnimationFrameRef.current);
            scrollAnimationFrameRef.current = null;
          }

          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }
          scrollTimeoutRef.current = setTimeout(() => {
            if (container && container.scrollHeight - container.scrollTop - container.clientHeight < 20) {
              isUserScrollingRef.current = false;
            }
          }, 1000);
        }
      };

      container.addEventListener('wheel', handleWheel, { passive: true });

      // Add touch support for mobile scrolling
      const handleTouchMove = () => {
        if (!isInitialLoadRef.current) {
          isUserScrollingRef.current = true;

          // Cancel all pending auto-scroll attempts when user touches
          pendingScrollTimeoutsRef.current.forEach(timeoutId => {
            clearTimeout(timeoutId);
          });
          pendingScrollTimeoutsRef.current = [];

          // Cancel any pending scroll animation frames
          if (scrollAnimationFrameRef.current) {
            cancelAnimationFrame(scrollAnimationFrameRef.current);
            scrollAnimationFrameRef.current = null;
          }

          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }
          scrollTimeoutRef.current = setTimeout(() => {
            if (container && container.scrollHeight - container.scrollTop - container.clientHeight < 20) {
              isUserScrollingRef.current = false;
            }
          }, 1000);
        }
      };

      container.addEventListener('touchmove', handleTouchMove, { passive: true });

      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('touchmove', handleTouchMove);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }

    // Listen to message deletion
    const removeMessageDeletedListener = ChatService.onMessageDeleted(room.roomId, (data) => {
      setMessages(prev => prev.filter(msg => {
        const msgId = msg.id || msg.messageId;
        return msgId !== data.messageId;
      }));
    });

    // Listen to chat cleared
    const removeChatClearedListener = ChatService.onChatCleared(room.roomId, (data) => {
      setMessages([]);
    });

    // Notify viewing room
    ChatService.notifyViewingRoom(room.roomId);

    // Request online users on mount (only if not using shared state)
    if (!sharedOnlineUsers) {
    ChatService.requestOnlineUsers();
    }

    // Listen to online status updates - only if not using shared state
    // If shared state is provided, parent (Chat.jsx) handles this
    let onlineStatusCallback = null;
    if (!sharedOnlineUsers) {
      onlineStatusCallback = (data) => {
        const userId = data.userId || data.user_id;
        const online = data.online === true || data.online === 1 || data.online === 'true';
        
        if (!userId) return;
        
        // Update online users Set (matches ChatListScreen)
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          const userIdStr = String(userId);
          
          // Remove any existing entry (handles type mismatches - string vs number)
          newSet.forEach(id => {
            if (String(id) === userIdStr) {
              newSet.delete(id);
            }
          });
          
          if (online) {
            // Add the userId (keep original type for consistency)
            newSet.add(userId);
          }
          // If offline, we've already removed it above
          
          // Update ref with latest Set
          onlineUsersRef.current = newSet;
          
          // Immediately check if this update affects the other participant (use ref for latest value)
          const currentOtherParticipant = otherParticipantRef.current;
          if (currentOtherParticipant) {
            const otherUserId = currentOtherParticipant.userId || currentOtherParticipant.user_id;
            if (otherUserId) {
              const otherUserIdStr = String(otherUserId);
              // Check multiple formats for matching
              const matches = (
                userIdStr === otherUserIdStr || 
                String(userId) === otherUserIdStr ||
                Number(userIdStr) === Number(otherUserIdStr) && !isNaN(Number(userIdStr)) && !isNaN(Number(otherUserIdStr))
              );
              
              if (matches) {
                setIsOtherUserOnline(online);
              }
            }
          }
          
          return newSet;
        });
      };
      
      ChatService.onOnlineStatus(onlineStatusCallback);
    }
    
    // Request online users immediately and multiple times to ensure we get the list (only if not using shared state)
    let statusCheckInterval = null;
    if (!sharedOnlineUsers) {
      ChatService.requestOnlineUsers();
      
      // Check after a short delay to allow socket events to process
      setTimeout(() => {
        ChatService.requestOnlineUsers();
      }, 300);
      
      // Also request after longer delay to catch any missed events
      setTimeout(() => {
        ChatService.requestOnlineUsers();
        // Force a status check after requesting (use ref to get latest Set)
        setTimeout(() => {
          const currentOtherParticipant = otherParticipantRef.current;
          const currentOnlineUsers = onlineUsersRef.current;
          if (currentOtherParticipant && currentOnlineUsers) {
            const otherUserId = currentOtherParticipant.userId || currentOtherParticipant.user_id;
            if (otherUserId) {
              const userIdStr = String(otherUserId);
              let isOnline = false;
              
              // Check current onlineUsers Set from ref
              currentOnlineUsers.forEach(id => {
                const idStr = String(id);
                if (idStr === userIdStr || 
                    (Number(idStr) === Number(userIdStr) && !isNaN(Number(idStr)) && !isNaN(Number(userIdStr)))) {
                  isOnline = true;
                }
              });
              
              setIsOtherUserOnline(isOnline);
            }
          }
        }, 500);
      }, 1500);
      
      // One more request to be sure
      setTimeout(() => {
        ChatService.requestOnlineUsers();
      }, 3000);
      
      // Also check periodically to ensure status is up to date
      statusCheckInterval = setInterval(() => {
        ChatService.requestOnlineUsers();
      }, 30000); // Check every 30 seconds
    } else {
      // If using shared state, periodically check status using the shared Set
      statusCheckInterval = setInterval(() => {
        const currentOtherParticipant = otherParticipantRef.current;
        const currentOnlineUsers = onlineUsersRef.current || sharedOnlineUsers;
        if (currentOtherParticipant && currentOnlineUsers) {
          const otherUserId = currentOtherParticipant.userId || currentOtherParticipant.user_id;
          if (otherUserId) {
            const userIdStr = String(otherUserId);
            let isOnline = false;
            
            currentOnlineUsers.forEach(id => {
              const idStr = String(id);
              if (idStr === userIdStr || 
                  (Number(idStr) === Number(userIdStr) && !isNaN(Number(idStr)) && !isNaN(Number(userIdStr)))) {
                isOnline = true;
              }
            });
            
            setIsOtherUserOnline(isOnline);
          }
        }
      }, 5000); // Check every 5 seconds when using shared state
    }
    
    // Cleanup online status listener (only if we registered one)
    const removeOnlineStatusListener = () => {
      if (onlineStatusCallback) {
        ChatService.removeOnlineStatusListener(onlineStatusCallback);
      }
    };

    // Setup app lifecycle handling (visibility change)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // App came to foreground
        ChatService.notifyUserOnline({
          userId: currentUserId,
          userName: currentUserName,
          userRole: currentUserRole,
          agencyId: room.agencyId || ''
        });
        ChatService.requestOnlineUsers();
      } else {
        // App went to background
        ChatService.notifyUserOffline({
          userId: currentUserId,
          userName: currentUserName,
          userRole: currentUserRole,
          agencyId: room.agencyId || ''
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle page unload
    const handleBeforeUnload = () => {
      ChatService.notifyUserOffline({
        userId: currentUserId,
        userName: currentUserName,
        userRole: currentUserRole,
        agencyId: room.agencyId || ''
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Request notification permission on mount
    browserNotifications.requestPermission();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Cleanup polling interval if it exists
      if (typeof pollInterval !== 'undefined') {
        clearInterval(pollInterval);
      }
      // Cleanup listeners
      ChatService.removeMessageListener(room.roomId);
      ChatService.removeTypingListener(room.roomId);
      ChatService.removeMessageReadStatusListener(room.roomId);
      removeMessageDeletedListener();
      removeChatClearedListener();
      removeOnlineStatusListener();
      // Clear status check interval if it exists
      if (typeof statusCheckInterval !== 'undefined') {
        clearInterval(statusCheckInterval);
      }
      ChatService.notifyNotViewingRoom(room.roomId);

      // Cleanup all scroll-related timeouts and animations
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      pendingScrollTimeoutsRef.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      pendingScrollTimeoutsRef.current = [];
      if (scrollAnimationFrameRef.current) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
        scrollAnimationFrameRef.current = null;
      }
    };
  }, [room.roomId]);

  useLayoutEffect(() => {
    // CRITICAL: Always scroll to bottom on initial load to show latest messages
    // Only skip auto-scroll if user is manually scrolling (and not initial load)
    if (isInitialLoadRef.current || (!isUserScrollingRef.current && !isLoading)) {
      // Cancel any pending scroll animations
      if (scrollAnimationFrameRef.current) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
      }

      // Use double requestAnimationFrame to ensure DOM is fully rendered
      scrollAnimationFrameRef.current = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Check again if user started scrolling
          if (isUserScrollingRef.current && !isInitialLoadRef.current) {
            return; // User is scrolling, don't interfere
          }

          const container = scrollContainerRef.current;
          const endElement = messagesEndRef.current;

          if (container) {
            // Mark that we're programmatically scrolling
            isScrollingProgrammaticallyRef.current = true;

            // Method 1: Direct scroll to bottom (fastest, most reliable)
            container.scrollTop = container.scrollHeight;

            // Method 2: Use scrollIntoView on end element (backup) - only if initial load
            if (endElement && isInitialLoadRef.current) {
              endElement.scrollIntoView({ behavior: 'auto', block: 'end', inline: 'nearest' });
            }

            // Verify we're at the bottom (only on initial load)
            if (isInitialLoadRef.current) {
              const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 10;
              if (!isAtBottom) {
                // Force scroll one more time if we're not at bottom on initial load
                const timeoutId = setTimeout(() => {
                  if (container && !isUserScrollingRef.current) {
                    isScrollingProgrammaticallyRef.current = true;
                    container.scrollTop = container.scrollHeight;
                  }
                }, 50);
                pendingScrollTimeoutsRef.current.push(timeoutId);
              } else {
                // Mark initial load as complete after successful scroll
                isInitialLoadRef.current = false;
              }
            }
          }
        });
      });
    }

    return () => {
      if (scrollAnimationFrameRef.current) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
        scrollAnimationFrameRef.current = null;
      }
    };
  }, [messages, isTyping, isLoading]);

  // CRITICAL: Scroll to bottom when loading completes
  useEffect(() => {
    // When loading finishes and we have messages, ALWAYS scroll to bottom
    if (!isLoading && messages.length > 0 && isInitialLoadRef.current) {

      // Disable user scroll detection temporarily during initial load
      isUserScrollingRef.current = false;

      // Reduced scroll attempts to prevent flickering - only 3 attempts
      const scrollAttempts = [0, 100, 300];

      scrollAttempts.forEach((delay, index) => {
        const timeoutId = setTimeout(() => {
          // Check if user started scrolling - if so, cancel
          if (isUserScrollingRef.current && !isInitialLoadRef.current) {
            return;
          }

          const container = scrollContainerRef.current;
          const endElement = messagesEndRef.current;

          if (container) {
            // Mark that we're programmatically scrolling
            isScrollingProgrammaticallyRef.current = true;

            // Force scroll to bottom
            container.scrollTop = container.scrollHeight;

            // Also use scrollIntoView (only on first attempt)
            if (endElement && index === 0) {
              endElement.scrollIntoView({ behavior: 'auto', block: 'end' });
            }

            // Verify we're at bottom (only on last attempt)
            if (index === scrollAttempts.length - 1) {
              const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 10;
              if (isAtBottom) {
                // Mark initial load complete only after we're confirmed at bottom
                isInitialLoadRef.current = false;
              }
            }
          }
        }, delay);

        // Track timeout so we can cancel it if user scrolls
        pendingScrollTimeoutsRef.current.push(timeoutId);
      });

      // Cleanup function to cancel timeouts
      return () => {
        pendingScrollTimeoutsRef.current.forEach(timeoutId => {
          clearTimeout(timeoutId);
        });
        pendingScrollTimeoutsRef.current = [];
      };
    }
  }, [isLoading, messages.length]);

  // Additional effect to ensure scroll to bottom after images/files load
  useEffect(() => {
    // Only scroll if initial load or user is not scrolling
    if (messages.length > 0 && (isInitialLoadRef.current || (!isUserScrollingRef.current && !isLoading))) {
      // Wait for images/files to load, then scroll again
      const timeoutId = setTimeout(() => {
        // Check if user started scrolling - if so, cancel
        if (isUserScrollingRef.current && !isInitialLoadRef.current) {
          return;
        }

        const container = scrollContainerRef.current;
        const endElement = messagesEndRef.current;

        if (container) {
          // Mark that we're programmatically scrolling
          isScrollingProgrammaticallyRef.current = true;

          // Force scroll to bottom after content loads
          container.scrollTop = container.scrollHeight;

          if (endElement && isInitialLoadRef.current) {
            endElement.scrollIntoView({ behavior: 'auto', block: 'end' });
          }
        }
      }, 500);

      pendingScrollTimeoutsRef.current.push(timeoutId);

      return () => {
        clearTimeout(timeoutId);
        const index = pendingScrollTimeoutsRef.current.indexOf(timeoutId);
        if (index > -1) {
          pendingScrollTimeoutsRef.current.splice(index, 1);
        }
      };
    }
  }, [messages.length, isLoading]);

  const loadMessages = async (skipLoadingState = false) => {
    try {
      // Only set loading state if not skipping (prevents blinking during polling)
      if (!skipLoadingState) {
        setIsLoading(true);
        // Reset scroll state for initial load (only on first load, not during polling)
        isInitialLoadRef.current = true;
        isUserScrollingRef.current = false;
      }

      const fetchedMessages = await ChatService.getMessages(room.roomId);
      // Filter out deleted messages (should already be filtered by ChatService, but double-check)
      const validMessages = fetchedMessages.filter(msg => msg.isDeleted !== true);

      // Process all messages through ChatService (handles file URLs and duplicates)
      const messagesWithFullUrls = validMessages.map(msg => {
        // CRITICAL: Fix messageType for voice recordings - backend might send wrong type
        if (msg.fileName && (msg.fileName.startsWith('voice_') || msg.fileName.match(/\.(webm|m4a|ogg|wav|mp3)$/i))) {
          if (msg.messageType !== 'audio' && msg.messageType !== 'voice') {
            console.warn('⚠️ Backend sent wrong messageType for voice recording in loadMessages, fixing:', {
              fileName: msg.fileName,
              originalMessageType: msg.messageType,
              correctedMessageType: 'audio'
            });
            msg.messageType = 'audio';
          }
        }
        
        // CRITICAL: Never store blob URLs - they're temporary and localhost-specific
        // If backend somehow returned blob URL, reject it
        if (msg.fileUrl && (msg.fileUrl.startsWith('blob:') || msg.fileUrl.startsWith('file://'))) {
          console.error('❌ CRITICAL: Backend returned blob/file URL in loaded messages!', {
            messageId: msg.id,
            fileUrl: msg.fileUrl,
            fileName: msg.fileName
          });
          // Remove blob URL - file cannot be displayed
          msg.fileUrl = null;
        } else if (msg.fileUrl) {
          // Process file URL (convert to full URL)
          msg.fileUrl = ChatService.getFullFileUrl(msg.fileUrl, room.roomId);
        }
        return msg;
      });

      // Only update if we got new messages or this is initial load
      setMessages(prev => {
        // If this is initial load, always update
        if (isInitialLoadRef.current) {
          return messagesWithFullUrls;
        }
        
        // For subsequent loads, only update if there are actually new messages
        const existingIds = new Set(prev.map(m => m.id || m.messageId));
        const newMessages = messagesWithFullUrls.filter(m => {
          const id = m.id || m.messageId;
          return id && !existingIds.has(id);
        });
        
        if (newMessages.length > 0) {
          return [...prev, ...newMessages];
        }
        
        // No new messages, don't update state (prevents blinking)
        return prev;
      });

      // Mark messages as read
      await ChatService.markMessagesAsReadAPI(room.roomId, currentUserId);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      // Only clear loading state if we set it (prevents blinking during polling)
      if (!skipLoadingState) {
        setIsLoading(false);
      }

      // CRITICAL: Force scroll to bottom AFTER loading completes and DOM is ready
      // This ensures messages are fully rendered before scrolling
      setTimeout(() => {
        const container = scrollContainerRef.current;
        const endElement = messagesEndRef.current;

        if (container) {
          // Method 1: Direct scroll (immediate)
          container.scrollTop = container.scrollHeight;

          // Method 2: scrollIntoView (backup)
          if (endElement) {
            endElement.scrollIntoView({ behavior: 'auto', block: 'end' });
          }

          // Method 3: Verify and re-scroll if needed
          requestAnimationFrame(() => {
            if (container) {
              const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 10;
              if (!isAtBottom) {
                container.scrollTop = container.scrollHeight;
                if (endElement) {
                  endElement.scrollIntoView({ behavior: 'auto', block: 'end' });
                }
              }
            }
          });
        }
      }, 0);

      // Additional scroll attempts for images/files that load later
      setTimeout(() => {
        const container = scrollContainerRef.current;
        if (container && !isUserScrollingRef.current) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);

      setTimeout(() => {
        const container = scrollContainerRef.current;
        if (container && !isUserScrollingRef.current) {
          container.scrollTop = container.scrollHeight;
        }
      }, 300);

      setTimeout(() => {
        const container = scrollContainerRef.current;
        if (container && !isUserScrollingRef.current) {
          container.scrollTop = container.scrollHeight;
        }
      }, 600);
    }
  };

  const joinRoom = () => {
    ChatService.joinRoom(room.roomId);
  };

  const setupListeners = () => {
    // CRITICAL: Remove old listener first to prevent duplicates
    ChatService.removeMessageListener(room.roomId);
    
    // Create listener callback - use useCallback pattern to ensure it's stable
    const messageListener = (message) => {
      // CRITICAL: Only process messages for this room
      if (message.roomId !== room.roomId) {
        return;
      }
      
      // Only play sound if message is from another user and page is visible
      const isMessageFromOtherUser = message.senderId !== currentUserId;
      const isPageVisible = !document.hidden;

      if (isMessageFromOtherUser && isPageVisible) {
        // Play notification sound (even in chat room, to alert user to new message)
        soundNotification.playNotification().catch(err => {
          console.warn('⚠️ Could not play notification sound:', err);
        });
      }

      // CRITICAL: Message is already processed in ChatService.message:received handler
      // We should NOT call handleMessageReceived again here as it will filter it as duplicate
      // Just ensure file URL is processed if needed
      if (message.fileUrl && !message.fileUrl.startsWith('http')) {
        // Process file URL to full URL if needed
        message.fileUrl = ChatService.getFullFileUrl(message.fileUrl, room.roomId);
      }

      // CRITICAL: Never store blob URLs from socket messages
      // If socket somehow sent blob URL, reject it
      if (message.fileUrl && (message.fileUrl.startsWith('blob:') || message.fileUrl.startsWith('file://'))) {
        console.error('❌ CRITICAL: Socket sent blob/file URL! This should never happen:', {
          messageId: message.id,
          fileUrl: message.fileUrl,
          fileName: message.fileName
        });
        // Remove blob URL - file cannot be displayed
        message.fileUrl = null;
      }

      // CRITICAL: Use functional update to ensure we have latest state
      // This ensures React detects the change and re-renders
      setMessages(prev => {
        const messageId = message.id || message.messageId;
        const isFromCurrentUser = message.senderId === currentUserId;

        // Safety check: if message with same ID already exists, skip
        // (Service-level duplicate detection should have caught this, but check anyway)
        const existingById = prev.findIndex(msg => {
          const existingId = msg.id || msg.messageId;
          return messageId && existingId && messageId === existingId;
        });

        if (existingById !== -1) {
          return prev;
        }

        let foundOptimisticIndex = -1;
        let matchingOptimisticKey = null;

        // CRITICAL: Match optimistic messages for CURRENT USER's messages
        // This prevents duplicate messages when server sends back the message we just sent
        if (isFromCurrentUser) {
          // Try 1: Match TEXT messages by content + sender + timestamp
          if (!message.fileName && message.message) {
            for (const [key, value] of pendingOptimisticMessagesRef.current.entries()) {
              // Match by message content (text messages)
              // CRITICAL: Trim whitespace for comparison to handle any formatting differences
              const optimisticMessage = (value.message || '').trim();
              const serverMessage = (message.message || '').trim();
              
              if (optimisticMessage && serverMessage && 
                  optimisticMessage === serverMessage &&
                  !value.fileName) { // Only match text messages
                
                const timeDiff = Math.abs(Date.now() - value.timestamp);
                if (timeDiff < 30000) { // Within 30 seconds (increased from 10 for reliability)
                  // Find optimistic message by temp ID
                  foundOptimisticIndex = prev.findIndex(msg => {
                    const isTempMessage = msg.id && msg.id.toString().startsWith('temp-');
                    const msgText = (msg.message || '').trim();
                    const messageMatches = msgText === serverMessage;
                    const senderMatches = msg.senderId === message.senderId;
                    const isTextMessage = !msg.fileName; // No file = text message
                    return isTempMessage && messageMatches && senderMatches && isTextMessage;
                  });

                  if (foundOptimisticIndex !== -1) {
                    matchingOptimisticKey = key;
                    break;
                  }
                }
              }
            }
          }
          
          // Try 2: Match FILE messages by filename (most reliable)
          // Flutter matches by: 1. Filename, 2. File URL, 3. Time + sender + messageType
          if (foundOptimisticIndex === -1 && message.fileName) {
            // Check pending optimistic messages by filename
            for (const [key, value] of pendingOptimisticMessagesRef.current.entries()) {
              if (value.fileName && message.fileName) {
              // Match if filenames match (exact or contains)
              const optimisticFileName = value.fileName.toLowerCase();
              const serverFileName = message.fileName.toLowerCase();

              if (optimisticFileName === serverFileName ||
                optimisticFileName.includes(serverFileName) ||
                serverFileName.includes(optimisticFileName)) {
                matchingOptimisticKey = key;

                // Find optimistic message by temp ID
                foundOptimisticIndex = prev.findIndex(msg => {
                  const isTempMessage = msg.id && msg.id.toString().startsWith('temp-');
                  const fileNameMatches = msg.fileName &&
                    (msg.fileName.toLowerCase() === optimisticFileName ||
                      msg.fileName.toLowerCase().includes(serverFileName) ||
                      serverFileName.includes(msg.fileName.toLowerCase()));
                  const senderMatches = msg.senderId === message.senderId;
                  return isTempMessage && fileNameMatches && senderMatches;
                });

                if (foundOptimisticIndex !== -1) break;
              }
            }
            }

            // Try 2: Match by fileUrl if filename match failed
            if (foundOptimisticIndex === -1 && message.fileUrl) {
              const serverFileUrlName = message.fileUrl.split('/').pop()?.toLowerCase();
              if (serverFileUrlName) {
              for (const [key, value] of pendingOptimisticMessagesRef.current.entries()) {
                foundOptimisticIndex = prev.findIndex(msg => {
                  const isTempMessage = msg.id && msg.id.toString().startsWith('temp-');
                  const fileUrlMatches = msg.fileUrl &&
                    (msg.fileUrl.includes(serverFileUrlName) ||
                      msg.fileUrl.startsWith('blob:') ||
                      msg.fileUrl.startsWith('file://'));
                  const senderMatches = msg.senderId === message.senderId;
                  return isTempMessage && fileUrlMatches && senderMatches;
                });

                if (foundOptimisticIndex !== -1) {
                  matchingOptimisticKey = key;
                  break;
                }
              }
            }
            }

            // Try 3: Match by time + sender + messageType (fallback)
            if (foundOptimisticIndex === -1) {
              for (const [key, value] of pendingOptimisticMessagesRef.current.entries()) {
              const timeDiff = Math.abs(Date.now() - value.timestamp);
              if (timeDiff < 20000 && value.messageType === message.messageType) { // Within 20 seconds
                foundOptimisticIndex = prev.findIndex(msg => {
                  const isTempMessage = msg.id && msg.id.toString().startsWith('temp-');
                  const timeMatches = Math.abs(new Date(msg.createdAt).getTime() - new Date(message.createdAt).getTime()) < 20000;
                  const senderMatches = msg.senderId === message.senderId;
                  const typeMatches = msg.messageType === message.messageType;
                  return isTempMessage && timeMatches && senderMatches && typeMatches;
                });

                if (foundOptimisticIndex !== -1) {
                  matchingOptimisticKey = key;
                  break;
                }
              }
            }
            }

            // Replace optimistic message if found
            if (foundOptimisticIndex !== -1 && matchingOptimisticKey) {
              // Remove from pending tracking
              pendingOptimisticMessagesRef.current.delete(matchingOptimisticKey);

              // Replace optimistic message with real message
              const realMessage = {
                ...message,
                isRead: message.isRead === true || message.isRead === 1,
                readAt: message.readAt ? new Date(message.readAt) : null
              };

              const newMessages = [...prev];
              newMessages[foundOptimisticIndex] = realMessage;
              return newMessages;
            }
          }
        }

        // Check for duplicate by content + sender + timestamp (for OTHER users' messages only)
        // For current user's messages, we already handled optimistic matching above
        if (!isFromCurrentUser) {
          const isDuplicate = prev.some(msg => {
            const existingId = msg.id || msg.messageId;
            // Skip temp messages in this check (they're from current user)
            if (msg.id && msg.id.toString().startsWith('temp-')) {
              return false;
            }
            // Check by content + sender + similar timestamp (within 2 seconds)
            if (msg.message === message.message &&
              msg.senderId === message.senderId) {
              const msgTime = msg.createdAt ? new Date(msg.createdAt).getTime() : 0;
              const newMsgTime = message.createdAt ? new Date(message.createdAt).getTime() : 0;
              const timeDiff = Math.abs(newMsgTime - msgTime);
              if (timeDiff < 2000) { // Within 2 seconds
                return true;
              }
            }
            return false;
          });

          if (isDuplicate) {
            return prev;
          }
        } else {
          // For current user's messages, if we didn't find optimistic match, 
          // check if there's already a message with same content (temp or real)
          // This is a safety check to prevent duplicates
          const existingMessage = prev.find(msg => {
            const sameContent = (msg.message || '').trim() === (message.message || '').trim();
            const sameSender = msg.senderId === message.senderId;
            const sameId = (msg.id || msg.messageId) === messageId;
            
            // Check by ID first (most reliable)
            if (sameId) return true;
            
            // Check by content + sender (for both temp and real messages)
            // This catches cases where optimistic matching failed
            if (sameContent && sameSender) {
              return true;
            }
            
            return false;
          });
          
          if (existingMessage) {
            return prev;
          }
        }

        // New message, add it - CRITICAL: This triggers React re-render
        // CRITICAL: Create new array reference - React detects this change
        // Ensure isRead and readAt are properly set from server
        // CRITICAL: Fix messageType for voice recordings - backend might send wrong type
        let correctedMessageType = message.messageType;
        if (message.fileName && (message.fileName.startsWith('voice_') || message.fileName.match(/\.(webm|m4a|ogg|wav|mp3)$/i))) {
          if (message.messageType !== 'audio' && message.messageType !== 'voice') {
            console.warn('⚠️ Backend sent wrong messageType for voice recording, fixing:', {
              fileName: message.fileName,
              originalMessageType: message.messageType,
              correctedMessageType: 'audio'
            });
            correctedMessageType = 'audio';
          }
        }
        
        const messageWithReadStatus = {
          ...message,
          messageType: correctedMessageType, // Use corrected messageType
          isRead: message.isRead === true || message.isRead === 1,
          readAt: message.readAt ? new Date(message.readAt) : (message.isRead ? new Date() : null)
        };
        
        const newMessages = [...prev, messageWithReadStatus];
        
        return newMessages;
      });

      // CRITICAL: Force React to re-render IMMEDIATELY
      // This ensures UI updates even if React doesn't detect the change automatically
      setForceUpdate(prev => prev + 1);
      
      // Scroll to bottom when new message arrives
      // Use requestAnimationFrame for smooth, flicker-free scrolling
      requestAnimationFrame(() => {
        scrollToBottom(true); // Force scroll for incoming messages
      });

      // Mark as read
      ChatService.markMessagesAsReadAPI(room.roomId, currentUserId);

      // Show browser notification if page is not visible
      if (!browserNotifications.isPageVisible() && message.senderId !== currentUserId) {
        browserNotifications.showNotification(message.senderName || 'New Message', {
          body: message.message || (message.fileName ? `Sent ${message.fileName}` : 'New message'),
          tag: message.roomId,
          onClick: () => {
            // Focus window when notification clicked
            window.focus();
          }
        });
      }
    };
    
    // Register the listener
    ChatService.onMessage(room.roomId, messageListener);
    
    // Verify listener was registered
    const verifyListener = ChatService.messageListeners?.get(room.roomId);
    if (!verifyListener) {
      // Try registering again immediately
      ChatService.onMessage(room.roomId, messageListener);
    }
    
    // CRITICAL: Also listen on socket connect event to re-register if needed
    // This ensures listener is always active when socket connects/reconnects
    if (ChatService.socket) {
      const handleSocketConnect = () => {
        const existingListener = ChatService.messageListeners?.get(room.roomId);
        if (!existingListener) {
          ChatService.onMessage(room.roomId, messageListener);
        }
        // Rejoin room on reconnect
        ChatService.joinRoom(room.roomId);
      };
      
      // Listen for connect events
      ChatService.socket.on('connect', handleSocketConnect);
      
      // If already connected, verify listener now
      if (ChatService.socket.connected) {
        setTimeout(handleSocketConnect, 100);
      }
    }

    // Listen to typing status
    ChatService.onTyping(room.roomId, (data) => {
      if (data.userId !== currentUserId) {
        setIsTyping(data.isTyping);
        setTypingUserName(data.isTyping ? data.userName : null);
      }
    });

    // Listen to message read status updates
    ChatService.onMessageReadStatus(room.roomId, (readStatusData) => {
      // Update messages when read status changes
      setMessages(prev => {
        let hasUpdates = false;
        const updatedMessages = prev.map(msg => {
          // Update messages that match the read status criteria
          // If readStatusData has messageIds, update those specific messages
          // Otherwise, update all messages in the room that are from current user
          if (readStatusData.messageIds && Array.isArray(readStatusData.messageIds)) {
            // Update specific messages by ID
            const msgId = msg.id || msg.messageId;
            // Convert both to strings for reliable comparison
            const msgIdStr = msgId ? msgId.toString() : null;
            const messageIdsStr = readStatusData.messageIds.map(id => id ? id.toString() : null);
            
            if (msgIdStr && messageIdsStr.includes(msgIdStr)) {
              // Update even if already read (to ensure UI reflects latest status)
              if (!msg.isRead || msg.isRead === false || msg.isRead === 0) {
                hasUpdates = true;
                return {
                  ...msg,
                  isRead: true,
                  readAt: readStatusData.readAt ? new Date(readStatusData.readAt) : new Date()
                };
              }
            }
          } else if (readStatusData.roomId === room.roomId && msg.senderId === currentUserId) {
            // Update all messages from current user in this room
            // Only if they were sent before the read time
            const readTime = readStatusData.readAt ? new Date(readStatusData.readAt).getTime() : Date.now();
            const msgTime = msg.createdAt ? new Date(msg.createdAt).getTime() : 0;

            if (msgTime <= readTime && !msg.isRead) {
              hasUpdates = true;
              return {
                ...msg,
                isRead: true,
                readAt: readStatusData.readAt ? new Date(readStatusData.readAt) : new Date()
              };
            }
          }
          return msg;
        });
        
        if (hasUpdates) {
          // Force re-render to show updated double tick
          setForceUpdate(prev => prev + 1);
        }
        
        return updatedMessages;
      });
    });
  };

  const handleSendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const receiverId = otherParticipant?.userId || '';
    const receiverName = otherParticipant?.userName || '';
    const receiverRole = otherParticipant?.userRole || '';

    // Optimistically add message to UI
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      roomId: room.roomId,
      senderId: currentUserId,
      senderName: currentUserName,
      senderRole: currentUserRole,
      receiverId,
      receiverName,
      receiverRole,
      message: messageText,
      messageType: 'text',
      isRead: false, // Optimistic messages are never read
      readAt: null,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Track this optimistic message for replacement
    const messageKey = `${messageText}_${currentUserId}_${Date.now()}`;
    pendingOptimisticMessagesRef.current.set(messageKey, {
      tempId,
      timestamp: Date.now(),
      message: messageText
    });

    setMessages(prev => [...prev, optimisticMessage]);

    // Scroll to bottom when sending a message
    // Use requestAnimationFrame for smooth, flicker-free scrolling
    requestAnimationFrame(() => {
      scrollToBottom(true); // Force scroll for sent messages
    });

    try {
      const result = await ChatService.sendMessage({
        roomId: room.roomId,
        senderId: currentUserId,
        senderName: currentUserName,
        senderRole: currentUserRole,
        receiverId,
        receiverName,
        receiverRole,
        message: messageText,
        messageType: 'text',
      });

      if (!result || !result.success) {
        throw new Error('Failed to send message');
      }

    } catch (error) {
      console.error('❌ Error sending message:', error);

      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      pendingOptimisticMessagesRef.current.delete(messageKey);

      // Show error to user
      alert('Failed to send message. Please check your connection and try again.');
    }

    // Clean up old pending messages (older than 30 seconds)
    setTimeout(() => {
      const now = Date.now();
      for (const [key, value] of pendingOptimisticMessagesRef.current.entries()) {
        if (now - value.timestamp > 30000) {
          pendingOptimisticMessagesRef.current.delete(key);
        }
      }
    }, 30000);
  };

  /**
   * Get duration from audio/video file
   */
  const getFileDuration = (file) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
        resolve(null);
        return;
      }

      const media = file.type.startsWith('audio/')
        ? new Audio()
        : document.createElement('video');

      media.onloadedmetadata = () => {
        resolve(Math.floor(media.duration));
      };

      media.onerror = () => {
        resolve(null);
      };

      media.src = URL.createObjectURL(file);
    });
  };

  /**
   * Handle file selection
   */
  const handleSendFile = async (file) => {
    if (!file) return;

    // File size validation (5MB limit)
    const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      alert(`File size (${fileSizeMB} MB) exceeds 5MB limit. Please select a smaller file.`);
      return;
    }

    // File type validation (extensions)
    const fileName = String(file.name || '');
    const ext = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
    const allowedImageExt = new Set(['jpg', 'jpeg', 'png']);
    const allowedDocExt = new Set(['pdf', 'doc', 'xls', 'xlsx']);
    const allowedMediaExt = new Set(['mp3', 'wav', 'mov']);
    const isAllowed =
      (ext && allowedImageExt.has(ext)) ||
      (ext && allowedDocExt.has(ext)) ||
      (ext && allowedMediaExt.has(ext));
    if (!isAllowed) {
      alert(
        'Invalid file type. Allowed:\n' +
          'Images: .jpg, .jpeg, .png\n' +
          'Documents: .pdf, .doc, .xls, .xlsx\n' +
          'Media: .mp3, .wav, .mov'
      );
      return;
    }

    // Determine message type - CRITICAL: Check filename FIRST for voice recordings
    // Some browsers may set video/webm for webm files, but voice_ files are ALWAYS audio
    let messageType = 'file';
    
    // FIRST: Check if it's a voice recording by filename (takes highest priority)
    if (allowedImageExt.has(ext)) {
      messageType = 'image';
    } else if (allowedDocExt.has(ext)) {
      messageType = 'file';
    } else if (ext === 'mov') {
      messageType = 'video';
    } else if (allowedMediaExt.has(ext)) {
      messageType = 'audio';
    }

    // For images, show preview modal with compression
    if (messageType === 'image') {
      try {
        // Compress image
        const compressedFile = await ImageCompression.compressImage(file, {
          quality: 0.85,
          maxWidth: 1920,
          maxHeight: 1920,
        });

        // Show preview modal
        setPreviewImage(compressedFile);
        setShowImagePreview(true);
      } catch (error) {
        console.error('❌ Error compressing image:', error);
        // If compression fails, use original file
        setPreviewImage(file);
        setShowImagePreview(true);
      }
    } else {
      // For non-image files, send directly
      await sendFileDirectly(file, messageType);
    }
  };

  /**
   * Send file directly (non-image files or after preview)
   */
  const sendFileDirectly = async (file, messageType, caption = null) => {
    // CRITICAL: roomId is REQUIRED for proper file storage
    // Files MUST be stored in api/chat/files/{roomId}/ format
    if (!room?.roomId) {
      console.error('❌ CRITICAL: room.roomId is missing! Cannot upload file without roomId');
      alert('Error: Cannot upload file - room information is missing. Please refresh the page.');
      return;
    }

    // CRITICAL: Verify messageType for voice recordings
    if (file.name.startsWith('voice_') || file.name.match(/\.(webm|m4a|ogg|wav|mp3)$/i)) {
      if (messageType !== 'audio') {
        console.warn('⚠️ Voice recording detected but messageType is not audio. Fixing...', {
          fileName: file.name,
          fileType: file.type,
          currentMessageType: messageType
        });
        messageType = 'audio';
      }
    }

    const receiverId = otherParticipant?.userId || '';
    const receiverName = otherParticipant?.userName || '';
    const receiverRole = otherParticipant?.userRole || '';

    // Declare variables outside try block for use in catch block
    const tempId = `temp-${Date.now()}`;
    const messageKey = `${file.name}_${currentUserId}_${Date.now()}`;
    let localFileUrl = null;

    try {
      // CRITICAL: Verify roomId format - must include agency ID (e.g., "4_CT123" not just "4")
      const fullRoomId = room.roomId;

      // Verify roomId has correct format (should contain underscore for room-specific storage)
      if (!fullRoomId || !fullRoomId.includes('_')) {
        console.error('❌ CRITICAL: roomId format is incorrect!', {
          roomId: fullRoomId,
          expectedFormat: 'userId_agencyId (e.g., 4_CT123)',
          actualFormat: fullRoomId,
          note: 'Files will be stored in wrong location if roomId is incomplete'
        });
        console.error('❌ roomId should be like "4_CT123" but got:', fullRoomId);
        alert('Error: Invalid room ID format. Files may be stored in wrong location. Please refresh the page.');
        return;
      }

      // Get duration for audio/video files
      let duration = null;
      if (messageType === 'audio' || messageType === 'video') {
        duration = await getFileDuration(file);
      }

      // Create optimistic message
      localFileUrl = URL.createObjectURL(file);

      // CRITICAL: Ensure messageType is 'audio' for voice recordings
      const finalMessageType = (file.name.startsWith('voice_') || file.type.startsWith('audio/')) 
        ? 'audio' 
        : messageType;

      const optimisticMessage = {
        id: tempId,
        roomId: room.roomId,
        senderId: currentUserId,
        senderName: currentUserName,
        senderRole: currentUserRole,
        receiverId,
        receiverName,
        receiverRole,
        message: caption || (finalMessageType === 'image' ? 'image file' :
          finalMessageType === 'audio' ? 'audio file' :
            finalMessageType === 'video' ? 'video file' :
              'file'),
        messageType: finalMessageType, // Use finalMessageType to ensure audio for voice recordings
        fileUrl: localFileUrl, // Local blob URL for immediate display
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        duration: duration,
        isRead: false,
        readAt: null,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Track for replacement
      pendingOptimisticMessagesRef.current.set(messageKey, {
        tempId,
        timestamp: Date.now(),
        fileName: file.name,
        messageType: finalMessageType // Use finalMessageType
      });

      // Add optimistic message to UI immediately
      setMessages(prev => [...prev, optimisticMessage]);

      // Try upload endpoint first - use verified fullRoomId
      const uploadResult = await ChatService.uploadFile(file, duration, fullRoomId);

      let result = null;

      // If upload endpoint doesn't exist, use messages endpoint directly
      if (uploadResult.useMessagesEndpoint === true || uploadResult.success === false) {
        try {
          result = await ChatService.sendFileMessageViaAPI({
            roomId: fullRoomId, // Use verified full roomId
            senderId: currentUserId,
            senderName: currentUserName,
            senderRole: currentUserRole,
            receiverId,
            receiverName,
            receiverRole,
            file: file,
            message: caption || optimisticMessage.message,
            duration: duration,
          });

          if (!result) {
            throw new Error('Failed to send file message via /messages endpoint');
          }
        } catch (apiError) {
          // Re-throw with better error information
          console.error('❌ Error from sendFileMessageViaAPI:', {
            message: apiError.message,
            status: apiError.status,
            response: apiError.response,
            data: apiError.data
          });
          throw apiError; // Re-throw to be caught by outer catch block
        }
      } else {
        // Upload succeeded, verify fileUrl exists before sending message
        if (!uploadResult.fileUrl) {
          console.error('❌ Upload succeeded but fileUrl is missing!', uploadResult);
          throw new Error('Upload succeeded but fileUrl is missing');
        }

        // Match Flutter: Don't verify file immediately - just use the URL from backend
        // Flutter doesn't verify files after upload, it just uses the URL

        // CRITICAL: Ensure we NEVER send blob URLs to backend
        // uploadResult.fileUrl should be server path like /api/chat/files/...
        // If it's a blob URL, something went wrong
        if (uploadResult.fileUrl && (uploadResult.fileUrl.startsWith('blob:') || uploadResult.fileUrl.startsWith('file://'))) {
          console.error('❌ CRITICAL: Upload returned blob URL instead of server path!', uploadResult);
          throw new Error('Upload returned invalid fileUrl - expected server path, got blob URL');
        }

        // Upload succeeded, now send message via socket or REST API
        // CRITICAL: Use finalMessageType to ensure audio type for voice recordings
        const sendMessageType = uploadResult.messageType || finalMessageType || messageType;
        
        result = await ChatService.sendMessage({
          roomId: fullRoomId, // Use verified full roomId
          senderId: currentUserId,
          senderName: currentUserName,
          senderRole: currentUserRole,
          receiverId,
          receiverName,
          receiverRole,
          message: caption || optimisticMessage.message,
          messageType: sendMessageType, // Use sendMessageType to ensure correct type
          fileUrl: uploadResult.fileUrl, // Server path only (e.g., /api/chat/files/...)
          fileName: uploadResult.fileName,
          fileSize: uploadResult.fileSize,
          fileType: uploadResult.fileType,
          duration: duration,
        });

        if (!result || !result.success) {
          throw new Error('Failed to send file message');
        }

        result = result.data; // Extract data from result

        // Verify result has fileUrl
        if (result && !result.fileUrl) {
          console.warn('⚠️ Message sent but result.fileUrl is missing, using uploadResult.fileUrl');
          result.fileUrl = uploadResult.fileUrl;
        }
      }

      // Update optimistic message with server response
      if (result) {
        // Get file URL from result (matches Flutter logic)
        // Flutter uses: uploadResult.data.fileUrl or result.fileUrl
        let serverFileUrl = result.fileUrl;

        // If using messages endpoint, result already has fileUrl
        // If using upload endpoint, uploadResult has fileUrl
        if (!serverFileUrl && uploadResult && uploadResult.fileUrl) {
          serverFileUrl = uploadResult.fileUrl;
        }

        // Ensure full URL (matches Flutter _getFullFileUrl logic)
        // Pass roomId to normalize URL if needed
        if (serverFileUrl) {
          serverFileUrl = ChatService.getFullFileUrl(serverFileUrl, fullRoomId);
        }

        // Match Flutter behavior: Use server URL directly, don't verify immediately
        // Flutter doesn't verify files after upload - it just uses the URL from backend
        // The image component will handle loading errors gracefully

        // CRITICAL: Always use server-generated filename (timestamp-based format)
        // Backend generates filenames like: 1766399626967-961779317-1766399625669.jpg
        // NEVER use original filename (file.name) - only use server-generated filename
        const serverFileName = result.fileName || uploadResult?.fileName;

        if (!serverFileName) {
          console.error('❌ CRITICAL: Server did not return fileName!', {
            result: result,
            uploadResult: uploadResult
          });
          throw new Error('Server did not return fileName - cannot proceed without server-generated filename');
        }

        setMessages(prev => prev.map(msg => {
          // Match by tempId first (most direct)
          if (msg.id === tempId) {
            return {
              ...msg,
              id: result.id,
              fileUrl: serverFileUrl, // Use server URL directly (like Flutter)
              fileName: serverFileName,
              fileSize: result.fileSize || uploadResult?.fileSize || file.size,
              fileType: result.fileType || uploadResult?.fileType || file.type,
              messageType: result.messageType || uploadResult?.messageType || finalMessageType || messageType,
            };
          }

          // Fallback: Match by filename if tempId doesn't match (matches Flutter behavior)
          if (msg.id && msg.id.toString().startsWith('temp-') &&
            msg.senderId === currentUserId &&
            msg.fileName && serverFileName) {
            const msgFileName = msg.fileName.toLowerCase();
            const serverFileNameLower = serverFileName.toLowerCase();

            if (msgFileName === serverFileNameLower ||
              msgFileName.includes(serverFileNameLower) ||
              serverFileNameLower.includes(msgFileName)) {
              return {
                ...msg,
                id: result.id,
                fileUrl: serverFileUrl,
                fileName: serverFileName, // CRITICAL: Always use server-generated filename
                fileSize: result.fileSize || uploadResult?.fileSize || file.size,
                fileType: result.fileType || uploadResult?.fileType || file.type,
                messageType: result.messageType || uploadResult?.messageType || finalMessageType || messageType,
              };
            }
          }

          return msg;
        }));

        // Clean up local blob URL after a short delay (let image component switch to server URL)
        // Flutter does this automatically when Image.network loads
        setTimeout(() => {
          if (localFileUrl) {
            URL.revokeObjectURL(localFileUrl);
          }
        }, 1000);
      }

      // Scroll to bottom
      setTimeout(() => {
        scrollToBottom(true);
      }, 100);
    } catch (error) {
      console.error('❌ Error sending file:', error);
      
      // Safely extract error details
      const errorDetails = {
        message: error.message || 'Unknown error',
        status: error.status || error.response?.status,
        responseData: error.data || error.response?.data,
        fileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type,
        roomId: room?.roomId
      };
      
      console.error('❌ Error details:', errorDetails);

      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      if (messageKey) {
        pendingOptimisticMessagesRef.current.delete(messageKey);
      }

      // Clean up local blob URL if it was created
      if (localFileUrl) {
        URL.revokeObjectURL(localFileUrl);
      }

      // Show specific error message
      let errorMessage = 'Failed to send file. ';
      
      // Safely access error properties
      const status = error.status || error.response?.status;
      const errorData = error.data || error.response?.data;
      const errorMsg = error.message || '';
      
      if (status) {
        if (status === 413) {
          errorMessage += 'File is too large. Please select a smaller file.';
        } else if (status === 400) {
          const serverMessage = errorData?.message || errorData?.error || errorMsg;
          errorMessage += serverMessage || 'Invalid file format or missing information. Please check the file and try again.';
        } else if (status === 401) {
          errorMessage += 'Authentication failed. Please refresh the page and try again.';
        } else if (status === 404) {
          errorMessage += 'Upload endpoint not found. Please check your connection.';
        } else if (status === 500) {
          errorMessage += 'Server error. Please try again later.';
        } else {
          const serverMessage = errorData?.message || errorData?.error || errorMsg;
          errorMessage += `Error ${status}: ${serverMessage || 'Please check your connection and try again.'}`;
        }
      } else if (errorMsg) {
        errorMessage += errorMsg;
      } else {
        errorMessage += 'Please check your connection and try again.';
      }
      
      alert(errorMessage);
    }
  };

  /**
   * Handle image preview send (with caption)
   */
  const handleImagePreviewSend = (caption) => {
    setShowImagePreview(false);
    if (previewImage) {
      sendFileDirectly(previewImage, 'image', caption);
      setPreviewImage(null);
    }
  };

  /**
   * Handle image preview cancel
   */
  const handleImagePreviewCancel = () => {
    setShowImagePreview(false);
    if (previewImage) {
      URL.revokeObjectURL(URL.createObjectURL(previewImage));
      setPreviewImage(null);
    }
  };

  const handleTyping = (isTyping) => {
    ChatService.sendTypingIndicator({
      roomId: room.roomId,
      userId: currentUserId,
      userName: currentUserName,
      isTyping,
    });
  };

  const scrollToBottom = (force = false) => {
    if (!scrollContainerRef.current) return;

    // Don't scroll if user is manually scrolling (unless forced or initial load)
    if (!force && !isInitialLoadRef.current && isUserScrollingRef.current) {
      return;
    }

    const container = scrollContainerRef.current;
    const scrollHeight = container.scrollHeight;
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;

    // Scroll if forced, near bottom, or initial load
    if (force || isNearBottom || isInitialLoadRef.current) {
      // Cancel any pending scroll animation
      if (scrollAnimationFrameRef.current) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
      }

      // Use requestAnimationFrame for smooth scrolling
      scrollAnimationFrameRef.current = requestAnimationFrame(() => {
        if (!scrollContainerRef.current) return;

        // Check again if user started scrolling
        if (isUserScrollingRef.current && !isInitialLoadRef.current && !force) {
          return;
        }

        const container = scrollContainerRef.current;
        const targetScroll = container.scrollHeight;

          // Mark that we're programmatically scrolling (reset after scroll completes)
          isScrollingProgrammaticallyRef.current = true;

          // CRITICAL: Immediate scroll to bottom - no animation for instant results
          container.scrollTop = targetScroll;

          // Reset programmatic flag after scroll completes
          setTimeout(() => {
            isScrollingProgrammaticallyRef.current = false;
          }, 100);

          // Also try scrollIntoView on end element for reliability (only if initial load or forced)
          if (messagesEndRef.current && (isInitialLoadRef.current || force)) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end', inline: 'nearest' });
          }
      });
    }
  };

  const getAvatarColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return theme.colors.roleAdmin;
      case 'officeadmin':
        return theme.colors.roleOfficeAdmin;
      case 'driver':
        return theme.colors.roleDriver;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getInitials = (name) => {
    // Handle undefined, null, or invalid names
    if (!name || name === 'undefined' || name === 'null' || typeof name !== 'string') {
      return '?';
    }

    const trimmed = name.trim();
    if (!trimmed || trimmed.length === 0) return '?';

    // Filter out "undefined" strings
    const parts = trimmed.split(' ').filter(part =>
      part && part !== 'undefined' && part !== 'null' && part.trim().length > 0
    );

    if (parts.length >= 2) {
      const first = parts[0][0]?.toUpperCase() || '';
      const second = parts[1][0]?.toUpperCase() || '';
      if (first && second) {
        return first + second;
      }
    }

    // Fallback: use first character
    const firstChar = trimmed[0]?.toUpperCase();
    return firstChar || '?';
  };

  // Determine if message is from current user (with team logic)
  const isMyMessage = (message) => {
    if (message.senderId === currentUserId) {
      return true;
    }
    // Team logic: admins see admin messages on right side
    if ((currentUserRole === 'admin' || currentUserRole === 'officeAdmin') &&
      (message.senderRole?.toLowerCase() === 'admin' ||
        message.senderRole?.toLowerCase() === 'officeadmin')) {
      return true;
    }
    return false;
  };

  // Get avatar colors based on role
  const getAvatarBgColor = (role) => {
    const colors = {
      'driver': { bg: '#fed7aa', border: '#fdba74', text: '#9a3412' },
      'officeadmin': { bg: '#e9d5ff', border: '#c084fc', text: '#6b21a8' },
      'admin': { bg: '#cffafe', border: '#67e8f9', text: '#164e63' },
    };
    return colors[role?.toLowerCase()] || { bg: '#f3f4f6', border: '#d1d5db', text: '#374151' };
  };

  const avatarColors = getAvatarBgColor(otherParticipant?.userRole);
  const displayName = otherParticipant?.userName || otherParticipant?.name || room.roomName || 'Chat';
  const initials = getInitials(displayName);
  const roleDisplay = otherParticipant?.userRole ? otherParticipant.userRole.charAt(0).toUpperCase() + otherParticipant.userRole.slice(1) : '';

  return (
    <div className="chat-room-screen">
      {/* Modern Header */}
      <div className="chat-room-header">
        <div className="header-user-info">
          {showBackButton && onBack && (
            <button 
              className="header-action-button" 
              onClick={onBack}
              style={{ marginRight: '0.5rem' }}
            >
              <span className="material-icons-outlined">arrow_back</span>
            </button>
          )}
          <div 
            className="header-avatar"
            style={{
              backgroundColor: avatarColors.bg,
              borderColor: avatarColors.border,
              color: avatarColors.text
            }}
          >
            {initials}
          </div>
          <div className="header-info">
            <h3 className="header-name">{displayName}</h3>
            <div className="header-status-row">
              {isOtherUserOnline ? (
                <span className="online-badge" data-status="online">Online</span>
              ) : (
                <span className="online-badge" data-status="offline">Offline</span>
              )}
              <span className="role-badge">• {roleDisplay}</span>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="header-action-button" 
            title="Search messages"
            onClick={() => setShowSearchBar(!showSearchBar)}
          >
            <span className="material-icons-outlined">search</span>
          </button>
          {/* s */}
        </div>
      </div>

      {/* Message Search Bar - Toggleable */}
      {showSearchBar && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: 'white',
          borderBottom: '1px solid #f5f5f4',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          animation: 'slideDown 0.2s ease-out'
        }}>
          <span className="material-icons-outlined" style={{ color: '#a8a29e', fontSize: '1.25rem' }}>search</span>
          <input
            type="text"
            placeholder="Search messages..."
            value={searchQuery}
            autoFocus
            onChange={(e) => {
              const query = e.target.value;
              setSearchQuery(query);
              if (query.trim()) {
                setIsSearching(true);
                const filtered = messages.filter(msg => {
                  const messageText = (msg.message || '').toLowerCase();
                  const fileName = (msg.fileName || '').toLowerCase();
                  return messageText.includes(query.toLowerCase()) || fileName.includes(query.toLowerCase());
                });
                setFilteredMessages(filtered);
              } else {
                setIsSearching(false);
                setFilteredMessages([]);
              }
            }}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '0.875rem',
              color: '#1c1917',
              backgroundColor: 'transparent'
            }}
          />
          <button
            onClick={() => {
              setSearchQuery('');
              setIsSearching(false);
              setFilteredMessages([]);
              setShowSearchBar(false);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#a8a29e',
              cursor: 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#78716c'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#a8a29e'}
          >
            <span className="material-icons-outlined" style={{ fontSize: '1.125rem' }}>close</span>
          </button>
        </div>
      )}

      {/* Messages Container */}
      <div className="chat-messages-container custom-scrollbar" ref={scrollContainerRef}>
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {/* Date Divider - Today */}
            <div className="date-divider">
              <span className="date-badge">
                Today, {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
              </span>
            </div>
            
            {/* Messages - Show filtered if searching, otherwise show all */}
            {(isSearching ? filteredMessages : messages).map((message, index) => {
              const isMe = isMyMessage(message);
              const allMessages = isSearching ? filteredMessages : messages;
              const showTimestamp = index === 0 ||
                (allMessages[index - 1] &&
                  new Date(message.createdAt) - new Date(allMessages[index - 1].createdAt) > 5 * 60 * 1000);

              return (
                <ChatMessageBubble
                  key={message.id || index}
                  message={message}
                  isMe={isMe}
                  currentUserId={currentUserId}
                  showTimestamp={showTimestamp}
                />
              );
            })}
            
            {isSearching && filteredMessages.length === 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem',
                color: '#78716c',
                fontSize: '0.875rem'
              }}>
                No messages found matching "{searchQuery}"
              </div>
            )}
          </>
        )}

        {isTyping && typingUserName && (
          <TypingIndicator userName={typingUserName} />
        )}

        {/* Scroll anchor - always at the bottom to ensure latest messages are visible */}
        <div
          ref={messagesEndRef}
          style={{
            height: '1px',
            width: '100%',
            flexShrink: 0
          }}
        />
      </div>

      {/* Input - Modern Design */}
      <div style={{
        flexShrink: 0,
        flexGrow: 0,
        width: '100%',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: 'white',
        borderTop: '1px solid #f5f5f4'
      }}>
        <ChatInputField
          onSendMessage={handleSendMessage}
          onTyping={handleTyping}
          onSendFile={handleSendFile}
        />
      </div>

      {/* Image Preview Modal */}
      {showImagePreview && previewImage && (
        <ImagePreviewModal
          image={previewImage}
          onSend={handleImagePreviewSend}
          onCancel={handleImagePreviewCancel}
        />
      )}

      {/* Media Gallery Modal */}
      {showMediaGallery && (
        <MediaGalleryModal
          mediaFiles={mediaFiles}
          roomId={room?.roomId}
          onClose={() => setShowMediaGallery(false)}
        />
      )}
    </div>
  );
};

export default ChatRoomScreen;

